import { differenceInCalendarWeeks, fromUnixTime } from "date-fns"
import { observer } from "mobx-react-lite"
import { useNavigate } from "react-router-dom"

import type { ILock } from "@/domains/lock"
import { TokenIcon } from "@/components"
import { Button } from "@/components/button"
import { ShowWhenTrue } from "@/components/conditionals"
import { dollars, formatCurrency, formatPercentage } from "@/domains/library"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useGovernanceToken } from "@/hooks/use-governance-token"
import { useGovernanceTokenPrice } from "@/hooks/use-governance-token-price"
import { useNativeToken } from "@/hooks/use-native-token"
import { useNativeTokenPrice } from "@/hooks/use-native-token-price"
import { useVestedToken } from "@/hooks/use-vested-token"

import { DisplayLevel } from "../../../../domains/common/components/display-level"

type NftCardProps = {
  nft: ILock
  veTotalSupply: number
  onMaxLockMultipleClick: (nftIds: Array<number>) => void
}

const NftCard = observer(({ nft, veTotalSupply, onMaxLockMultipleClick }: NftCardProps) => {
  const governanceToken = useGovernanceToken()
  const vestedToken = useVestedToken()
  const chainConfig = useChainConfig()
  const navigate = useNavigate()
  const governanceTokenPrice = useGovernanceTokenPrice()
  const nativeToken = useNativeToken()
  const nativeTokenPrice = useNativeTokenPrice()

  const nftGovernanceValueInDollars = governanceTokenPrice * Number(nft.lockAmount)
  const nftGovernanceValueInNativeTokens = nftGovernanceValueInDollars / nativeTokenPrice
  const power = Number(nft.lockAmount) > 0 ? Number(nft.lockValue) / Number(nft.lockAmount) : 0
  const influence = Number(nft.lockAmount) / Number(veTotalSupply)
  const expiryWeeks = differenceInCalendarWeeks(fromUnixTime(nft.lockEnds), new Date())

  return (
    <div className="flex flex-col gap-4 rounded-lg bg-p300 p-4 shadow-expand-btn-shadow md:p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <ShowWhenTrue when={chainConfig.slug === "base"}>
            <TokenIcon token={governanceToken} square={44} calledFrom="NftCard" />
          </ShowWhenTrue>
          <div>
            <p className="mb-1 text-base font-medium text-t100">#{nft.id}</p>
            <p className="text-xs text-t300">{vestedToken.symbol} NFT</p>
          </div>
        </div>
        <DisplayLevel lockValue={Number(nft.lockValue)} />
      </div>
      <div className="flex w-full items-center gap-3">
        <p className="text-sm text-t100">Power</p>
        <div className="h-5 flex-1 rounded-md bg-p300-light shadow-progress-shadow">
          <ShowWhenTrue when={!nft.hasExpired}>
            <div
              className="flex h-full items-center justify-center rounded-md border border-p200-light bg-p200 px-2 text-center shadow-progress-blue-shadow"
              style={{ width: `${power * 100}%` }}
            >
              <p className="caption text-xs text-t100">{formatPercentage(power, 2)}</p>
            </div>
          </ShowWhenTrue>
          <ShowWhenTrue when={nft.hasExpired}>
            <div className="flex h-full items-center justify-center rounded-md bg-p400 px-2 text-center">-</div>
          </ShowWhenTrue>
        </div>
      </div>
      <div className="space-y-2 rounded-md bg-p300-light p-4 text-sm text-t100">
        <div className="flex items-center justify-between gap-3">
          <p>Balance</p>
          <p>
            {formatCurrency(Number(nft.lockAmount), 4)} <span className="text-t300">{governanceToken.symbol}</span>
          </p>
        </div>
        <div className="flex items-center justify-between gap-3 text-sm text-t100">
          <p>Votes</p>
          <p>
            {formatCurrency(Number(nft.lockValue), 4)} <span className="text-t300">{vestedToken.symbol}</span>
          </p>
        </div>
        <div className="flex items-center justify-between gap-3 text-sm text-t100">
          <p>Influence</p>
          <p>{formatPercentage(influence, 4)}</p>
        </div>
      </div>
      <div className=" space-y-2 rounded-md bg-p400 p-4 text-xs text-t100 ">
        <p className=" text-t200">Locked {governanceToken.symbol} value:</p>
        <div className="flex items-center justify-between gap-3 text-sm">
          <p>
            {dollars(nftGovernanceValueInDollars, 2)} <span className="text-t200">USD</span>
          </p>
          <span className="text-t300">|</span>
          <p>
            {formatCurrency(nftGovernanceValueInNativeTokens, 2)}{" "}
            <span className="text-t200">{nativeToken.symbol}</span>
          </p>
        </div>
      </div>

      <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
        <div className=" flex w-full items-center gap-2 rounded-md bg-p400 px-4 py-2 sm:w-auto">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16">
            <path stroke="#7C89A6" strokeMiterlimit="10" d="M8 14A6 6 0 108 2a6 6 0 000 12z"></path>
            <path stroke="#7C89A6" strokeLinecap="round" strokeLinejoin="round" d="M8 4.5V8h3.5"></path>
          </svg>
          <p className="text-sm text-t100">
            <ShowWhenTrue when={nft.hasExpired}>Expired</ShowWhenTrue>
            <ShowWhenTrue when={!nft.hasExpired}>{expiryWeeks} weeks</ShowWhenTrue>
          </p>
        </div>
        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-end md:gap-3">
          <Button
            className="whitespace-nowrap"
            variant="neutral"
            disabled={nft.isMaxLocked || nft.hasExpired}
            onClick={() => onMaxLockMultipleClick([nft.id])}
          >
            Max Lock
          </Button>
          <Button
            variant="neutral"
            onClick={() => {
              navigate(`/lock/${nft.id}/increase-amount`)
            }}
          >
            Manage
          </Button>
        </div>
      </div>
    </div>
  )
})

export default NftCard
